import React, { PureComponent } from "react";
import { connect } from "react-redux";
import {
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Paper,
  Typography
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

import { withErrorBoundary } from "BaseApp/ErrorBoundary/ErrorBoundary";
import DesignTargetHelper from "MetaComponent/helper/DesignTarget";

import DesignTargetApi from "MetaComponent/api/DesignTarget";
import DirectoryExplorerApi from "MetaComponent/api/DirectoryExplorer";
import FamilyApi from "MetaCell/api/Family";
import MaterialApi from "MetaCell/api/Material";
import SetPointApi from "MetaComponent/api/SetPoint";
import SelectedDesignTargetApi from "MetaComponent/api/SelectedDesignTarget";

import DesignTargetSelector from "MetaComponent/selectors/DesignTarget";
import DirectoryExplorerSelector from "MetaComponent/selectors/DirectoryExplorer";
import { FamilySelector } from "MetaCell/selectors/Family";
import MaterialSelector from "MetaCell/selectors/Material";
import SelectedDesignTargetSelector from "MetaComponent/selectors/SelectedDesignTarget";
import SetPointSelector from "MetaComponent/selectors/SetPoint";

import ConfirmDialogAction from "BaseApp/actions/ConfirmDialog";
import DirectionSnackbar from "components/Snackbar/Snackbar";
import EditActions from "components/EditActions/EditActions";
import IconTooltip from "components/IconTooltip/IconTooltip";
import MembersView from "./components/MembersView/MembersView";
import MembersSelection from "./components/MembersSelection/MembersSelection";
import { IdealComponent } from "./components/IdealComponent/IdealComponent";

const styles = theme => ({
  root: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
    margin: 30
  },
  title: {
    float: "left"
  }
});

/**
 * A class component to connect to redux state and serve the form. See {@link MetaCellGlobalParametersForm}
 * @typedef {Component} MetaCellGlobalParameters
 * @author Akira Kotsugai
 */
export class ComponentMembers extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      editingSelectedFM: null,
      saving: false,
      snackbar: {
        visible: false,
        message: ""
      },
      isIdealComponent: false,
      metaCellConfigData: [],
      materialsData: {},
      incidentLightData: {},
      familyMembersStructures: null
    };
  }

  componentDidMount = async () => {
    if (this.props.openMetaComponentId !== -1) {
      this.props.fetchMaterialsAction();
      const openMetaComponent = this.getOpenMetaComponent();
      this.setState({
        ...this.getIdealComponentData(),
        isIdealComponent: Boolean(openMetaComponent.ideal_family)
      });
    }
  };

  /**
   * it saves unsaved editings for the current page  and fetches all data when the selected simulation changes
   * @param {Object} prevProps - the previous props
   */
  componentDidUpdate = async prevProps => {
    const { openMetaComponentId } = this.props;
    if (
      openMetaComponentId !== prevProps.openMetaComponentId &&
      openMetaComponentId !== -1
    ) {
      this.props.fetchMaterialsAction();
      const openMetaComponent = this.getOpenMetaComponent();

      this.setState({
        ...this.getIdealComponentData(),
        isIdealComponent: Boolean(openMetaComponent.ideal_family)
      });
    }
  };

  getIdealComponentData() {
    const openMetaComponent = this.getOpenMetaComponent();
    const { materials, setPoints } = this.props;
    const materialsValues = materials ? Object.values(materials.byId) : [];
    const defaultMaterials = [
      materialsValues.find(material => material?.name === "SiO2") || null,
      materialsValues.find(material => material?.name === "Air") || null
    ];

    let metaCellConfigData = {
      id: 1,
      position: 1,
      unit: "μm",
      num_metacells: 16,
      width: 0.5,
      height: 0.5
    };
    let materialsData = [
      {
        id: 1,
        position: 1,
        name: "Incident material",
        material: defaultMaterials[0]?.id
      },
      {
        id: 2,
        position: 2,
        name: "Exit material",
        material: defaultMaterials[1]?.id
      }
    ];
    let incidentLightData = {
      id: 1,
      diffractive_order: { X: 0, Y: 0 },
      incident_light: {
        zenit: 0,
        azimut: 0,
        wavelength: openMetaComponent.unit === "nm" ? 550 : 0.55,
        amplitude: 1,
        polarization: 0,
        incident_light_type: "PW",
        focal_spot_x: 0,
        focal_spot_y: 0,
        focal_spot_z: 0,
        beam_divergence: 0,
        polarization: "TE"
      },
      unit: openMetaComponent.unit
    };

    const idealFamily = openMetaComponent.ideal_family;
    if (!Boolean(idealFamily)) {
      return {
        metaCellConfigData: [metaCellConfigData],
        materialsData: materialsData,
        incidentLightData: incidentLightData
      };
    }

    metaCellConfigData.width = idealFamily.cell_width;
    metaCellConfigData.height = idealFamily.cell_height;
    metaCellConfigData.unit = idealFamily.unit;
    metaCellConfigData.num_metacells = idealFamily.num_metacells;

    const idealMaterials = Object.values(idealFamily.materials || {});
    idealMaterials.forEach((material, index) => {
      if (!materialsData[index]) return;
      materialsData[index].material = material?.id || null;
      const refractive = material?.refractiveIndex;
      materialsData[index].refractiveIndex = Number.isInteger(refractive)
        ? refractive
        : null;
      const absorption = material?.absorptionCoeff;
      materialsData[index].absorptionCoeff = Number.isInteger(absorption)
        ? absorption
        : null;
    });

    if (idealFamily.set_point) {
      const setPoint = setPoints.byId[idealFamily.set_point];
      incidentLightData.unit = setPoint?.unit;
      incidentLightData.incident_light = setPoint?.incident_light;
      incidentLightData.incident_light.incident_light_type =
        setPoint?.incident_light_type;
    }

    return {
      metaCellConfigData: [metaCellConfigData],
      materialsData: materialsData,
      incidentLightData: incidentLightData
    };
  }

  /**
   * it updates the editing selected family members with new ones
   * @param {Number[]} newSelectedFM - an array of family member Id"s
   * @callback
   */
  updateSelectedFM = newSelectedFM => {
    this.setState({
      editingSelectedFM: newSelectedFM
    });
  };

  /**
   * it changes the editing state with the open meta component selected family members
   */
  editSelectedFM = () => {
    const openMetaComponent = this.getOpenMetaComponent(),
      { selectedFM } = openMetaComponent;
    this.setState({
      editingSelectedFM: selectedFM ? selectedFM : []
    });
  };

  /**
   * it changes the editing state to null
   */
  cancelEditingSelectedFM = () => {
    this.setState({
      editingSelectedFM: null
    });
    this.setPointFormReset && this.setPointFormReset();

    const isIdeal = Boolean(this.getOpenMetaComponent().ideal_family);
    this.setState({
      ...this.getIdealComponentData(),
      isIdealComponent: isIdeal
    });
  };

  /**
   * @param {Object} metaComponent
   * @returns {Object} the family that is linked to the component
   */
  getComponentFamily(metaComponent) {
    const { families } = this.props;
    return { ...families.byId[metaComponent.family] };
  }

  validateIdealComponent(metaCellConfigData, materialsData) {
    const metaCell = metaCellConfigData && metaCellConfigData[0];
    if (!metaCell) {
      return "Meta cell configuration is missing.";
    }
    if (!metaCell.width || isNaN(metaCell.width)) {
      return "Meta cell width is required.";
    }
    if (!metaCell.height || isNaN(metaCell.height)) {
      return "Meta cell height is required.";
    }
    // Check materialsData
    if (!Array.isArray(materialsData) || materialsData.length < 2) {
      return "Both incident and exit materials must be specified.";
    }
    for (let i = 0; i < materialsData.length; i++) {
      const material = materialsData[i];
      if (!material.material) {
        return `Material selection is required for "${material.name}".`;
      }
    }
    return "";
  }

  checkCellDimensions = async (sdtObjs, componentDimensions) => {
    const designTargetObjs = sdtObjs.map(sdt =>
      this.props.designTargets
        ? this.props.designTargets.byId[sdt.design_target]
        : false
    );
    const designTargetsThatDontHaveFFWF = DesignTargetHelper.getDesignTargetsThatDontHaveFFWF(
      designTargetObjs
    );
    let thereAreDiscrepanciesToHandle = false;
    let messages = [];

    if (designTargetsThatDontHaveFFWF.length > 0) {
      const { families, familyMembers } = this.props,
        {
          editingSelectedFM,
          isIdealComponent,
          metaCellConfigData
        } = this.state;
      let upToDateDesignTargets = [];
      let familyMemberFamily =
        families.byId[familyMembers?.byId[editingSelectedFM[0]]?.family];
      if (familyMemberFamily === undefined && !isIdealComponent) return true;
      if (familyMemberFamily === undefined && isIdealComponent) {
        // at this point the ideal family doesn't exist yet, so we just pass
        // data provided by the user
        familyMemberFamily = {
          cell_width: metaCellConfigData[0].width,
          cell_height: metaCellConfigData[0].height,
          unit: metaCellConfigData[0].unit
        };
      }
      for (const dt of designTargetsThatDontHaveFFWF) {
        const upToDateDT = await DesignTargetApi.requestDesignTargets(dt.id);
        upToDateDesignTargets.push(...upToDateDT);
      }
      // TODO add information about the possibility to update design targets on design target page
      messages = await DesignTargetHelper.getDiscrepantDesignTargetNFWFsMessages(
        upToDateDesignTargets,
        familyMemberFamily,
        componentDimensions
      );
      thereAreDiscrepanciesToHandle = messages.some(message => message !== "");
    }
    if (thereAreDiscrepanciesToHandle) {
      messages.push(
        "\n\nYou can also recreate all selected design targets in bulk on Design Targets page."
      );
    }
    return new Promise(resolve => {
      if (thereAreDiscrepanciesToHandle) {
        const { showConfirmDialog } = this.props;
        showConfirmDialog(
          "Meta component size and near-field wavefront size",
          messages,
          () => resolve(true),
          () => resolve(false),
          false,
          () => resolve(false),
          undefined,
          false
        );
      } else {
        resolve(true);
      }
    });
  };

  /**
   * it saves the selected family members and leaves the editing mode
   */
  save = async () => {
    const {
        updateMetaComponent,
        openMetaComponentId,
        familyMembers,
        fetchFamiliesAction,
        fetchFamilyMembersAction,
        fetchSetPointsAction,
        fetchMaterialsAction,
        fetchSelectedDesignTargetsAction,
        selectedDesignTargets
      } = this.props,
      {
        editingSelectedFM,
        isIdealComponent,
        metaCellConfigData,
        materialsData
      } = this.state,
      openMetaComponent = this.getOpenMetaComponent(),
      { selected_design_targets, width, height, unit } = openMetaComponent,
      componentDimensions = { width, height, unit },
      sdtObjs = selected_design_targets.map(dtId =>
        selectedDesignTargets ? selectedDesignTargets.byId[dtId] : false
      );
    this.setState({ saving: true, snackbar: { message: "", visible: false } });

    const passedCheck = await this.checkCellDimensions(
      sdtObjs,
      componentDimensions
    );

    if (passedCheck) {
      try {
        if (this.setPointFormSubmit) {
          await this.setPointFormSubmit();
        }
        let metaComponentData = {
          is_ideal: isIdealComponent,
          selectedFM: editingSelectedFM,
          family:
            editingSelectedFM.length > 0
              ? familyMembers.byId[editingSelectedFM[0]].family
              : null
        };
        if (isIdealComponent) {
          const incidentLightData = this.state.incidentLightData;
          const error = this.validateIdealComponent(
            metaCellConfigData,
            materialsData
          );
          if (error) {
            this.setState({
              saving: false,
              snackbar: {
                message: error,
                visible: true
              }
            });
            return;
          }
          metaComponentData = {
            is_ideal: isIdealComponent,
            metacell_config_data: {
              metacells: { ...metaCellConfigData["0"] },
              materials: { ...materialsData }
            },
            incident_light_data: incidentLightData
          };
        }
        await updateMetaComponent(openMetaComponentId, metaComponentData);
        await fetchFamiliesAction();
        await fetchFamilyMembersAction();
        await fetchSelectedDesignTargetsAction(openMetaComponentId);
        if (isIdealComponent) {
          await fetchSetPointsAction();
          await fetchMaterialsAction();
          let newState = {
            editingSelectedFM: null,
            saving: false
          };
          if (selected_design_targets) {
            newState.snackbar = {
              visible: true,
              message:
                "Please update the selected incident light on the design targets to the 'Ideal metacell setting'"
            };
          }
          this.setState(newState);
          this.setPointFormReset && this.setPointFormReset();
          return;
        }
      } catch (e) {
        const errorMessage = e?.response?.data[0];
        this.setState({
          snackbar: {
            message: errorMessage,
            visible: true
          }
        });
      }
    }

    this.cancelEditingSelectedFM();
    this.setState({
      saving: false
    });
  };

  /**
   * @returns {Object} the open meta component entity
   */
  getOpenMetaComponent = () => {
    const { openMetaComponentId, metaComponents } = this.props;
    return metaComponents.byId[openMetaComponentId];
  };

  /**
   * it gets the ids of the meta component"s selected family members and builds an array
   * of family member objects from it
   * @returns {Object[]} an array of component members
   */
  getComponentMembers = () => {
    const { familyMembers } = this.props,
      openMetaComponent = this.getOpenMetaComponent(),
      { selectedFM } = openMetaComponent;
    return selectedFM
      .map(memberId => familyMembers.byId[memberId])
      .filter(member => member !== undefined); // don"t include the deleted members
  };

  handleBindSubmitForm = submitFn => {
    this.setPointFormSubmit = submitFn;
  };

  handleBindResetForm = resetFn => {
    this.setPointFormReset = resetFn;
  };

  render() {
    const { classes, families } = this.props,
      { editingSelectedFM, saving } = this.state,
      isEditing = editingSelectedFM !== null,
      openMetaComponent = this.getOpenMetaComponent(),
      family = openMetaComponent.family
        ? families.byId[openMetaComponent.family]
        : null;

    let content = null;
    let checkbox = (
      <FormGroup row={true} style={{ marginBottom: "10px" }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              onClick={e => {
                // if the meta component is ideal and the user wants to change that,
                // we reset editingSelectedFM, because it contains the ideal family
                // which is no longer valid
                if (this.state.isIdealComponent && !e.target.checked) {
                  this.setState({ editingSelectedFM: [] });
                }
                this.setState({ isIdealComponent: e.target.checked });
              }}
              checked={this.state.isIdealComponent}
              color="primary"
              style={{ paddingStart: "0px" }}
            />
          }
          label="Ideal meta component"
          style={{ marginRight: "0px" }}
        />
        <IconTooltip
          text={
            "Create an ideal meta component with ideal meta cells to test the wavefronts directly."
          }
        />
      </FormGroup>
    );

    if (this.state.isIdealComponent) {
      content = (
        <>
          {isEditing ? checkbox : ""}
          <IdealComponent
            isEditing={isEditing}
            metaCellConfigData={this.state.metaCellConfigData}
            setMetaCellConfigData={data =>
              this.setState({ metaCellConfigData: data })
            }
            materialsData={this.state.materialsData}
            setMaterialsData={data => this.setState({ materialsData: data })}
            incidentLightData={this.state.incidentLightData}
            setIncidentLightData={data =>
              this.setState({ incidentLightData: data })
            }
            materials={this.props.materials}
            bindSubmitForm={this.handleBindSubmitForm}
            bindResetForm={this.handleBindResetForm}
          />
        </>
      );
    } else if (isEditing) {
      content = (
        <>
          {checkbox}
          <MembersSelection
            families={this.props.families}
            editingSelectedFM={editingSelectedFM}
            familyMembers={this.props.familyMembers}
            onSelectFM={this.updateSelectedFM}
          />
        </>
      );
    } else {
      content = (
        <MembersView family={family} membersList={this.getComponentMembers()} />
      );
    }

    return (
      <>
        <Paper className={classes.root}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div test-data="titleWrapper">
                <Typography
                  className={classes.title}
                  variant="h5"
                  component="h3"
                >
                  Component Selected Meta cells
                </Typography>
                <IconTooltip
                  text={
                    "Selected Meta cells (meta atoms) which will be used to build this component"
                  }
                />
              </div>
              <div style={{ float: "right" }}>
                <EditActions
                  isEditing={isEditing}
                  isSaving={saving}
                  onEdit={this.editSelectedFM}
                  onCancel={this.cancelEditingSelectedFM}
                  onSave={this.save}
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              {content}
            </Grid>
          </Grid>
        </Paper>
        {this.state.snackbar.visible && (
          <DirectionSnackbar message={this.state.snackbar.message} />
        )}
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    openMetaComponentId: DirectoryExplorerSelector.getMetaComponentOpenId(
      state
    ),
    metaComponents: DirectoryExplorerSelector.getMetaComponents(state),
    familyMembers: FamilySelector.getFamilyMembers(state),
    families: FamilySelector.getFamilies(state),
    designTargets: DesignTargetSelector.getDesignTargets(state),
    selectedDesignTargets: SelectedDesignTargetSelector.getSelectedDesignTargets(
      state
    ),
    materials: MaterialSelector.getMaterials(state),
    setPoints: SetPointSelector.getSetPoints(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateMetaComponent: (id, newProperties) =>
      dispatch(DirectoryExplorerApi.updateMetaComponent(id, newProperties)),
    fetchFamiliesAction: () => dispatch(FamilyApi.fetchFamilies()),
    fetchFamilyMembersAction: () => dispatch(FamilyApi.fetchFamilyMembers()),
    fetchSelectedDesignTargetsAction: metaComponentId =>
      dispatch(
        SelectedDesignTargetApi.fetchSelectedDesignTargets(metaComponentId)
      ),
    fetchMaterialsAction: () => dispatch(MaterialApi.fetchMaterials()),
    fetchSetPointsAction: () => dispatch(SetPointApi.fetchSetPoints()),
    showConfirmDialog: (
      title,
      message,
      confirmAction,
      cancelAction,
      isReduxAction,
      postConfirm,
      inputPlaceholders,
      uploadFile,
      confirmLabels
    ) =>
      dispatch(
        ConfirmDialogAction.show(
          title,
          message,
          confirmAction,
          cancelAction,
          isReduxAction,
          postConfirm,
          inputPlaceholders,
          uploadFile,
          confirmLabels
        )
      )
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorBoundary(withStyles(styles)(ComponentMembers)));
